/**
 * The one control bank (card-preference-pickers-redesign brief § 4) — two groups, the knobs
 * (hints/learning toggles) and the card-preference pickers, replacing the old two-row split. Wide,
 * `justify-content: space-between` on the bank itself spreads the two GROUPS to its edges — the
 * bank owns the distribution, not either group internally (rulings round 1 § Item 1,
 * scratch/rulings-round-1.md: an earlier version gave the knobs group its own `flex-grow: 1` to
 * reach this same spread, which forced it to claim the bank's full width at every viewport and
 * guaranteed the pickers group could never share the line). Narrow, it wraps between the two
 * GROUPS rather than interleaving individual controls (each group is its own atomic flex item —
 * `.game-start-knobs-group`/`.game-start-pickers-group`, skat.css § Game Start — so the browser
 * never splits a group's own children across two lines). The pickers group is entirely absent
 * under English (`buildPickersGroup`'s DE/EN gate, brief § 6) — the bank then holds just the knobs
 * group, content-sized like always, which now hugs the bank's left edge rather than filling it (see
 * `.game-start-knobs-group`'s own CSS comment for why that specific trade was made).
 *
 * Split out of `game-start-ui.js` (CODING.md § Size Limits — that module crossed the size budget
 * on this brief's addition) — a self-contained composition unit, mirroring the directory's
 * existing split shape (`game-start-toggles-etch.js`, `game-start-panel-etch.js`,
 * `game-start-layout.js` — each its own composition/placement concern). Two importers, both
 * package-internal: `game-start-ui.js` takes `buildControlBank`, and the profile overlay's
 * settings sections (`game-start-profile-sections.js`) take `refreshControlBank` for the re-press
 * after a start-context write, so nothing outside the package reaches in (package Boundary, this
 * directory's CLAUDE.md). `refreshPickersGroup` (below) is exported for its own test rather than
 * for a caller — `refreshControlBank` is its only production caller, and the second-surface
 * staleness case it fixes is driven directly from `game-start-control-bank.test.js`.
 */
import { t, getActiveLanguage } from "../i18n.js";
import { createToggle, pressToggle } from "../toggle.js";
import {
  buildSortOrderPicker,
  buildRankStylePicker,
  refreshPickerSelections,
} from "../card-preference-pickers.js";
import { invalidateCardTemplates } from "../cards/card-template-cache.js";
import { track } from "../analytics.js";

export function buildControlBank(settings) {
  const bank = document.createElement("div");
  bank.className = "game-start-control-bank";
  bank.appendChild(buildKnobsGroup(settings));
  const pickers = buildPickersGroup();
  if (pickers != null) bank.appendChild(pickers);
  return bank;
}

/**
 * Re-presses the whole bank from the passed settings — the start-screen half of the profile
 * overlay's write path (profile-overlay-and-menu-slim § Settings ownership). DOM-only: the bank's
 * own closures keep writing the same shared settings object they already received, so this holds
 * no state of its own and never fires a control's `onToggle`/`onSelect`.
 *
 * The learning row's `disabled` class comes back too, not just the knob positions: that coupling
 * is what `createHintsToggle` maintains live, and a re-press alone would leave the row looking
 * usable after an overlay-side hints-off. No-op with no bank mounted (in-game, and every unit
 * test that exercises the overlay without a start screen behind it).
 */
export function refreshControlBank(settings) {
  const knobs = document.querySelector(".game-start-knobs-group");
  if (knobs == null) return;
  const learningGroup = knobs.querySelector(".learning-mode-group");
  // Selected by marker class rather than child index — the same structural-binding reasoning
  // refreshPickersGroup's own `data-pref` marks carry (below).
  pressToggle(
    knobs.querySelector(".toggle-group:not(.learning-mode-group)"),
    settings.hints,
  );
  pressToggle(learningGroup, settings.learningMode);
  learningGroup.classList.toggle("disabled", !settings.hints);
  refreshPickersGroup();
}

/**
 * Re-presses the inline pickers group's own selection state — the fix for the staleness rulings
 * round 1 § Item 2 (scratch/rulings-round-1.md) found: a second surface carries its own copy of
 * the same two pickers (the profile overlay's Karten section since profile-overlay-and-menu-slim,
 * the pause menu's picker section before it), and changing a pref there doesn't reach this
 * separate DOM subtree without an explicit nudge. `refreshControlBank` above is its only caller
 * now — the pickers are one half of what goes stale, the knobs the other. No-op when the group
 * isn't mounted at all (English, brief § 6 — there's nothing to keep in sync there since the
 * inline row never renders).
 * Each section is found by the `data-pref` mark `buildPickersGroup` stamps on it, not by position:
 * this used to destructure `querySelectorAll(".segmented-picker")` and so depended silently on the
 * append order below — reorder the two appends and the two prefs would refresh as each other, with
 * only a comment standing between the reader and the bug.
 */
export function refreshPickersGroup() {
  const group = document.querySelector(".game-start-pickers-group");
  if (group == null) return;
  refreshPickerSelections(
    group.querySelector('[data-pref="sort"]'),
    group.querySelector('[data-pref="rank"]'),
  );
}

function buildKnobsGroup(settings) {
  const group = document.createElement("div");
  group.className = "game-start-knobs-group";
  const learningGroup = createLearningToggle(settings);
  const hintsGroup = createHintsToggle(settings, learningGroup);
  group.appendChild(hintsGroup);
  group.appendChild(learningGroup);
  return group;
}

/**
 * The two card-preference pickers (card-preferences design § Controls) — DE/EN visibility gate,
 * brief § 6: the WHOLE group is omitted under English (not just the rank picker), since start-
 * screen prominence was earned by German demand and EN carries sort order through the menu
 * instead. `buildRankStylePicker`'s own DE-only gate stays the module's own separate knowledge
 * (it also governs the menu mount, which has no language gate of its own). Sort has no live effect
 * to chase — the next fetch/deal just decodes with the new value; rank re-warms the face-template
 * cache so a post-flip deal doesn't clone stale glyphs. Joins the toggles' frame-etch on
 * entrance (the ROWS group, `game-start-toggles-etch.js`), not the name reveal — brief § 5.
 */
function buildPickersGroup() {
  if (getActiveLanguage() !== "de") return null;
  const group = document.createElement("div");
  group.className = "game-start-pickers-group";
  // data-pref is what refreshPickersGroup finds each section by — a structural binding rather than
  // the append order it used to read positionally. Nothing styles these; they exist to be queried.
  const sortPicker = buildSortOrderPicker({});
  sortPicker.dataset.pref = "sort";
  group.appendChild(sortPicker);
  const rankPicker = buildRankStylePicker({
    onAfterChange: invalidateCardTemplates,
  });
  if (rankPicker != null) {
    rankPicker.dataset.pref = "rank";
    group.appendChild(rankPicker);
  }
  return group;
}

/**
 * Hints master toggle — persists `hints` to localStorage and disables the
 * learning toggle when hints are off.
 */
function createHintsToggle(settings, learningGroup) {
  return createToggle(t("gameStartHints"), settings.hints, (active) => {
    settings.hints = active;
    localStorage.setItem("hints", active ? "true" : "false");
    learningGroup.classList.toggle("disabled", !active);
    track("setting-toggled", {
      setting: "hints",
      value: active ? "on" : "off",
      screen: "start",
    });
  });
}

/** Learning mode toggle — persists `instantHints` to localStorage. Disabled when hints are off. */
function createLearningToggle(settings) {
  const group = createToggle(
    t("gameStartLearningMode"),
    settings.learningMode,
    (active) => {
      settings.learningMode = active;
      localStorage.setItem("instantHints", active ? "true" : "false");
      track("setting-toggled", {
        setting: "learning-mode",
        value: active ? "on" : "off",
        screen: "start",
      });
    },
  );
  group.classList.add("learning-mode-group");
  group.classList.toggle("disabled", !settings.hints);
  return group;
}
